import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import {
  Envelope, ArrowsClockwise, Lightning, CircleNotch, CheckCircle,
  XCircle, PaperPlaneRight, PencilSimple, Clock, Eye, EyeSlash, CaretRight, Translate, Trash,
  BookOpen, MagnifyingGlass, X, Package, FileText, Question, Star, Paperclip,
  Funnel, ListChecks, Sparkle
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TranslateButton } from '../components/TranslateButton';
import OrderEolPanel from '../components/inbox/OrderEolPanel';
import CollapsibleSection from '../components/CollapsibleSection';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REVIEW_STATUS_STYLES = {
  nieuw: { bg: 'bg-zinc-100 text-zinc-600', label: 'Nieuw' },
  wacht_op_goedkeuring: { bg: 'bg-amber-100 text-amber-800', label: 'Wacht op review' },
  goedgekeurd: { bg: 'bg-emerald-100 text-emerald-800', label: 'Goedgekeurd' },
  verzonden: { bg: 'bg-blue-100 text-blue-800', label: 'Verzonden' },
  afgewezen: { bg: 'bg-red-100 text-red-800', label: 'Afgewezen' },
};

export const CATEGORY_STYLES = {
  interest: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Interesse' },
  question: { bg: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Vraag' },
  quote_request: { bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: 'Offerte' },
  order: { bg: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Bestelling' },
  complaint: { bg: 'bg-red-50 text-red-700 border-red-200', label: 'Klacht' },
  out_of_office: { bg: 'bg-zinc-50 text-zinc-500 border-zinc-200', label: 'Afwezig' },
  unsubscribe: { bg: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Afmelden' },
  delivery_failure: { bg: 'bg-red-50 text-red-600 border-red-200', label: 'Bounce' },
  spam: { bg: 'bg-zinc-50 text-zinc-400 border-zinc-200', label: 'Spam' },
  other: { bg: 'bg-zinc-50 text-zinc-600 border-zinc-200', label: 'Overig' },
};

export const URGENCY_COLORS = {
  high: 'text-red-600 font-semibold',
  medium: 'text-amber-600',
  low: 'text-zinc-400',
};

export const INTENT_LABELS = {
  positive_intent: '🟢 Interesse',
  question: '🟡 Vraag',
  objection_price: '🟠 Prijs',
  unsubscribe: '🔴 Unsub',
  out_of_office: '⚪ OOO',
  other: '⚫ Overig',
};

export const INTENT_PILL_STYLES = {
  positive_intent: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  question: 'bg-amber-50 text-amber-700 border-amber-300',
  objection_price: 'bg-orange-50 text-orange-700 border-orange-300',
  unsubscribe: 'bg-rose-50 text-rose-700 border-rose-300',
  out_of_office: 'bg-zinc-100 text-zinc-500 border-zinc-300',
  other: 'bg-zinc-50 text-zinc-600 border-zinc-200',
};

export const INTENT_ICONS = {
  order: { icon: Package, color: 'text-purple-600 bg-purple-50 border-purple-200', title: 'Bestelling' },
  quote_request: { icon: FileText, color: 'text-indigo-600 bg-indigo-50 border-indigo-200', title: 'Offerteaanvraag' },
  question: { icon: Question, color: 'text-cyan-600 bg-cyan-50 border-cyan-200', title: 'Vraag' },
  interest: { icon: Star, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', title: 'Interesse' },
};

export function IntentIconBadge({ intent }) {
  const cfg = INTENT_ICONS[intent];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 rounded-sm border ${cfg.color}`}
      title={cfg.title}
      data-testid={`intent-icon-${intent}`}
    >
      <Icon size={10} weight="bold" />
    </span>
  );
}

function AttachmentPickerModal({ replyId, currentDocId, onClose, onPicked }) {
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState('');

  useEffect(() => {
    fetch(`${API}/knowledge-base?limit=200`, { credentials: 'include' })
      .then(r => r.json()).then(d => setDocs(d.documents || [])).catch(() => {});
  }, []);

  const filtered = docs.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.filename || '').toLowerCase().includes(q)
      || (d.description || '').toLowerCase().includes(q)
      || (d.category || '').toLowerCase().includes(q);
  });

  const pickDoc = async (docId) => {
    setPicking(docId);
    try {
      const res = await fetch(`${API}/email/inbox/${replyId}/attachment`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc_id: docId }),
      });
      if (res.ok) onPicked(await res.json());
    } catch { /* ignore */ }
    setPicking('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="attachment-picker-modal">
      <div className="bg-white w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-[#0055FF]" />
            <h3 className="font-heading text-base font-semibold">Kies een document uit de Kennisbank</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100" data-testid="close-picker"><X size={18} /></button>
        </div>
        <div className="px-5 py-3 border-b border-zinc-100">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="attachment-picker-search"
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Zoek op naam, categorie of beschrijving..."
              className="w-full pl-8 pr-3 py-2 border border-zinc-200 text-sm rounded-sm focus:ring-1 focus:ring-[#0055FF] focus:border-[#0055FF]"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-zinc-400 py-8">Geen documenten gevonden.</div>
          ) : filtered.map(d => {
            const isCurrent = d.doc_id === currentDocId;
            return (
              <button
                key={d.doc_id}
                onClick={() => pickDoc(d.doc_id)}
                disabled={!!picking}
                data-testid={`pick-doc-${d.doc_id}`}
                className={`w-full text-left flex items-start gap-3 px-3 py-2.5 border transition-colors disabled:opacity-50 ${isCurrent ? 'border-[#0055FF] bg-blue-50/50' : 'border-zinc-200 hover:bg-zinc-50'}`}
              >
                <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center text-zinc-500 text-[10px] font-bold shrink-0 mt-0.5">{(d.extension || '').replace('.', '').toUpperCase() || 'DOC'}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-800 truncate">{d.filename}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                    <Badge className="rounded-sm px-1.5 py-0 text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200">{d.category || 'overig'}</Badge>
                    {d.word_count ? <span>{d.word_count} woorden</span> : null}
                  </div>
                  {d.description ? <div className="text-xs text-zinc-400 mt-0.5 line-clamp-1">{d.description}</div> : null}
                </div>
                {picking === d.doc_id && <CircleNotch size={16} className="animate-spin text-[#0055FF] mt-2 shrink-0" />}
                {isCurrent && picking !== d.doc_id && <span className="text-[10px] text-[#0055FF] font-medium mt-2 shrink-0">Huidig</span>}
              </button>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-zinc-100 text-[11px] text-zinc-400">
          Het geselecteerde document wordt automatisch vertaald naar de doeltaal van de klant en als PDF bijgevoegd.
        </div>
      </div>
    </div>
  );
}

export function InboxDetailPanel({ reply, onClose, onAction }) {
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [translatedOriginal, setTranslatedOriginal] = useState('');
  const [translatedDraft, setTranslatedDraft] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [removingAttachment, setRemovingAttachment] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [previewAttachmentIdx, setPreviewAttachmentIdx] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Fetch attachment with credentials and convert to blob URL voor inline preview
  // (anders worden cookies niet meegestuurd in <iframe src>).
  useEffect(() => {
    if (previewAttachmentIdx === null || !reply?.incoming_attachments?.[previewAttachmentIdx]) {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
      return;
    }
    const att = reply.incoming_attachments[previewAttachmentIdx];
    const url = `${API}/email/inbox/${reply.reply_id}/incoming-attachment/${encodeURIComponent(att.attachment_id)}`;
    let cancelled = false;
    let blobUrlLocal = null;
    setPreviewLoading(true);
    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => {
        if (cancelled || !blob) return;
        blobUrlLocal = URL.createObjectURL(blob);
        setPreviewBlobUrl(blobUrlLocal);
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => {
      cancelled = true;
      if (blobUrlLocal) URL.revokeObjectURL(blobUrlLocal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewAttachmentIdx, reply?.reply_id]);

  const backfillBody = async () => {
    setBackfilling(true);
    try {
      const r = await fetch(`${API}/email/inbox/${reply.reply_id}/backfill-body`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        window.alert(err.detail || 'Volledig ophalen mislukt');
      } else {
        onAction?.('regenerated'); // hergebruik keep-open refresh
      }
    } catch {
      window.alert('Verbindingsfout bij ophalen origineel');
    }
    setBackfilling(false);
  };

  const regenerateDraft = async () => {
    if (!window.confirm('Het huidige AI-concept wordt overschreven met een nieuw concept op basis van de actuele AI-richtlijnen. Doorgaan?')) return;
    setRegenerating(true);
    try {
      const r = await fetch(`${API}/email/inbox/${reply.reply_id}/regenerate-draft`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        window.alert(err.detail || 'Herschrijven mislukt');
      } else {
        onAction?.('regenerated');
      }
    } catch {
      window.alert('Verbindingsfout');
    }
    setRegenerating(false);
  };

  useEffect(() => {
    if (reply?.draft_response) {
      setEditSubject(reply.draft_response.subject || '');
      setEditBody(reply.draft_response.body_text || '');
      setAttachment(reply.draft_response.attachment || null);
    } else {
      setAttachment(null);
    }
    setEditing(false);
    setShowReject(false);
    setRejectNotes('');
    setTranslatedOriginal('');
    setTranslatedDraft('');
    setPreviewUrl('');
    setPreviewKey(0);
  }, [reply]);

  if (!reply) return null;

  const cls = reply.ai_classification || {};
  const intent = reply.intent_classification || {};
  const draft = reply.draft_response;
  const catStyle = CATEGORY_STYLES[cls.category] || CATEGORY_STYLES.other;
  const reviewStyle = REVIEW_STATUS_STYLES[reply.review_status] || REVIEW_STATUS_STYLES.nieuw;

  const doAction = async (action, body = {}) => {
    setActionLoading(action);
    try {
      const res = await fetch(`${API}/email/inbox/${reply.reply_id}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      onAction(data);
    } catch { /* ignore */ }
    setActionLoading('');
  };

  const handlePickerPicked = (newAtt) => {
    setAttachment(newAtt);
    setShowPicker(false);
    setPreviewUrl('');
    setPreviewKey(k => k + 1);
  };

  const handleRemoveAttachment = async () => {
    if (!window.confirm('Weet je zeker dat je deze bijlage wilt verwijderen uit het concept?')) return;
    setRemovingAttachment(true);
    try {
      const res = await fetch(`${API}/email/inbox/${reply.reply_id}/attachment`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) { setAttachment(null); setPreviewUrl(''); }
    } catch { /* ignore */ }
    setRemovingAttachment(false);
  };

  const handleTogglePreview = () => {
    if (previewUrl) {
      setPreviewUrl('');
    } else {
      setPreviewUrl(`${API}/email/inbox/${reply.reply_id}/attachment/preview?k=${previewKey}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl border-l border-zinc-200 p-6"
        onClick={e => e.stopPropagation()} data-testid="inbox-detail-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Badge className={`rounded-sm px-2 py-0.5 text-[10px] font-medium border ${catStyle.bg}`}>{catStyle.label}</Badge>
            <Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${reviewStyle.bg}`}>{reviewStyle.label}</Badge>
            {reply.received_mailbox && <Badge className="text-[10px] bg-zinc-100 text-zinc-500">{reply.received_mailbox}</Badge>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100">&times;</button>
        </div>

        <h3 className="font-heading text-base font-semibold mb-1">{reply.subject}</h3>
        <div className="text-xs text-zinc-500 mb-4">Van: {reply.from_name || ''} &lt;{reply.from_email}&gt; | {reply.received_at ? new Date(reply.received_at).toLocaleString('nl-NL') : ''}</div>

        {/* AI Classification */}
        <div className="bg-zinc-50 border border-zinc-200 p-3 mb-4 text-xs space-y-1">
          <div><strong>Samenvatting:</strong> {cls.summary || '-'}</div>
          <div><strong>Sentiment:</strong> {cls.sentiment} | <strong>Urgentie:</strong> {cls.urgency} | <strong>Taal:</strong> {cls.detected_language}</div>
          {(cls.secondary_intents && cls.secondary_intents.length > 0) && (
            <div data-testid="secondary-intents">
              <strong>Aanvullende intenties:</strong>{' '}
              {cls.secondary_intents.map(s => (
                <Badge key={s} className="rounded-sm px-1.5 py-0 text-[10px] mr-1 bg-amber-50 text-amber-700 border border-amber-200">{s}</Badge>
              ))}
            </div>
          )}
        </div>

        {/* Linked Quotes & Orders (multi-intent) */}
        {((reply.linked_orders && reply.linked_orders.length > 0) || (reply.linked_quotes && reply.linked_quotes.length > 0)) && (
          <div className="mb-4 space-y-3" data-testid="linked-items-block">
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Auto-aangemaakt op deze e-mail</div>
            {reply.linked_orders?.map(o => (
              <OrderEolPanel
                key={o.order_id}
                order={o}
                onUpdate={(updated) => {
                  // Mutate in place so the parent reply object reflects new EOL state
                  if (reply.linked_orders) {
                    const idx = reply.linked_orders.findIndex(x => x.order_id === updated.order_id);
                    if (idx >= 0) reply.linked_orders[idx] = updated;
                  }
                }}
              />
            ))}
            {reply.linked_quotes?.map(q => {
              const qItems = q.items || [];
              const unmatched = qItems.filter(i => !i.article_number).length;
              return (
              <div key={q.quote_id} className={`flex items-start gap-3 p-2.5 border rounded-sm ${unmatched > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-indigo-200 bg-indigo-50/40'}`} data-testid={`linked-quote-${q.quote_id}`}>
                <Badge className="rounded-sm px-1.5 py-0.5 text-[10px] bg-indigo-100 text-indigo-700 border border-indigo-200 shrink-0">Offerte</Badge>
                <div className="flex-1 min-w-0 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-zinc-800">{q.reference || q.quote_id}</span>
                    {unmatched > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-yellow-200 text-yellow-900 border border-yellow-300 font-bold" data-testid={`quote-unmatched-${q.quote_id}`}>
                        ! {unmatched} zonder catalogus-match
                      </span>
                    )}
                  </div>
                  <div className="text-zinc-600 mt-1 space-y-0.5">
                    {qItems.length === 0 ? <span className="text-zinc-400">Geen items</span> : qItems.map((i, idx) => (
                      <div key={idx} className={`font-data ${!i.article_number ? 'text-yellow-900' : ''}`}>
                        <span className="font-bold">{i.article_number || '— geen match —'}</span>
                        <span className="mx-1.5 text-zinc-400">·</span>
                        <span>{i.description || i.article_name || ''}</span>
                        <span className="mx-1.5 text-zinc-400">·</span>
                        <span>{i.quantity}× {i.unit || 'stuk'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Original email — groeit mee met de inhoud, scrollbar pas bij erg lange mails */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
            <div className="text-xs font-semibold text-zinc-500 uppercase flex items-center gap-2">
              Origineel bericht
              {(reply.body_text || '').length > 0 && (reply.body_text || '').length <= 260 && reply.graph_message_id && !reply.body_backfilled_at && (
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm bg-amber-100 text-amber-800 border border-amber-200 font-bold"
                  title="Deze email is ingekort weergegeven — klik 'Volledig ophalen' rechts"
                >
                  Ingekort
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {reply.graph_message_id && (
                <button
                  type="button"
                  data-testid="backfill-body-btn"
                  onClick={backfillBody}
                  disabled={backfilling}
                  className="text-[11px] text-indigo-700 hover:text-indigo-900 disabled:opacity-50 flex items-center gap-1"
                  title="Haal de volledige originele body en bijlage-lijst opnieuw op via Microsoft Graph"
                >
                  {backfilling ? <CircleNotch size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />}
                  {backfilling ? 'Ophalen...' : 'Volledig ophalen'}
                </button>
              )}
              <TranslateButton text={reply.body_text || reply.body_html} sourceLang={cls.detected_language}
                onTranslated={setTranslatedOriginal} />
            </div>
          </div>
          <div
            data-testid="original-email-body"
            className="bg-white border border-zinc-200 p-3 text-sm text-zinc-700 whitespace-pre-wrap max-h-[60vh] overflow-y-auto"
          >
            {translatedOriginal || reply.body_text || reply.body_html || '-'}
          </div>

          {/* Inkomende bijlagen */}
          {Array.isArray(reply.incoming_attachments) && reply.incoming_attachments.length > 0 && (
            <div className="mt-2 space-y-2" data-testid="incoming-attachments">
              <div className="flex flex-wrap gap-2">
                {reply.incoming_attachments.map((att, i) => {
                  const sizeKb = att.size ? Math.max(1, Math.round(att.size / 1024)) : null;
                  const isPdf = (att.content_type || '').includes('pdf') || (att.filename || '').toLowerCase().endsWith('.pdf');
                  const isImg = (att.content_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(att.filename || '');
                  const canPreview = isPdf || isImg;
                  const url = `${API}/email/inbox/${reply.reply_id}/incoming-attachment/${encodeURIComponent(att.attachment_id)}`;
                  const isOpen = previewAttachmentIdx === i;
                  return (
                    <div key={att.attachment_id || i} className="inline-flex items-stretch border border-zinc-200 rounded-sm bg-zinc-50 hover:bg-indigo-50 hover:border-indigo-200 transition-colors">
                      {canPreview ? (
                        <button
                          type="button"
                          onClick={() => setPreviewAttachmentIdx(isOpen ? null : i)}
                          data-testid={`preview-att-${i}`}
                          className="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs hover:text-indigo-700"
                          title={isOpen ? 'Verberg preview' : 'Toon preview'}
                        >
                          {isOpen ? <EyeSlash size={12} className="text-indigo-600 shrink-0" /> : <Eye size={12} className="text-zinc-500 shrink-0" />}
                          <Paperclip size={12} className="shrink-0 text-zinc-500" />
                          <span className="truncate max-w-[260px]">{att.filename}</span>
                          {sizeKb && <span className="text-[10px] text-zinc-400 shrink-0">{sizeKb} KB</span>}
                        </button>
                      ) : (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`incoming-att-${i}`}
                          className="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs hover:text-indigo-700"
                          title={att.content_type || 'Download'}
                        >
                          <Paperclip size={12} className="shrink-0 text-zinc-500" />
                          <span className="truncate max-w-[260px]">{att.filename}</span>
                          {sizeKb && <span className="text-[10px] text-zinc-400 shrink-0">{sizeKb} KB</span>}
                        </a>
                      )}
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        download={att.filename}
                        title="Open in nieuw tabblad / download"
                        data-testid={`download-att-${i}`}
                        className="px-2 border-l border-zinc-200 flex items-center text-zinc-500 hover:text-indigo-700"
                      >
                        <CaretRight size={11} />
                      </a>
                    </div>
                  );
                })}
              </div>

              {/* Inline preview pane */}
              {previewAttachmentIdx !== null && reply.incoming_attachments[previewAttachmentIdx] && (() => {
                const att = reply.incoming_attachments[previewAttachmentIdx];
                const url = `${API}/email/inbox/${reply.reply_id}/incoming-attachment/${encodeURIComponent(att.attachment_id)}`;
                const isPdf = (att.content_type || '').includes('pdf') || (att.filename || '').toLowerCase().endsWith('.pdf');
                const isImg = (att.content_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(att.filename || '');
                return (
                  <div className="border border-indigo-200 rounded-sm bg-zinc-50 overflow-hidden" data-testid="attachment-preview-pane">
                    <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-indigo-50/60 border-b border-indigo-200 text-xs">
                      <div className="font-medium text-indigo-900 truncate flex items-center gap-1.5">
                        <Paperclip size={11} /> {att.filename}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPreviewAttachmentIdx(null)}
                        data-testid="close-attachment-preview"
                        className="text-indigo-600 hover:text-indigo-900 p-0.5"
                      >
                        <X size={13} />
                      </button>
                    </div>
                    {isPdf ? (
                      previewLoading || !previewBlobUrl ? (
                        <div className="bg-white p-8 flex items-center justify-center text-sm text-zinc-500" style={{ height: '70vh' }}>
                          <CircleNotch size={20} className="animate-spin mr-2" /> PDF laden...
                        </div>
                      ) : (
                        <iframe
                          title={`pdf-preview-${att.filename}`}
                          src={previewBlobUrl}
                          className="w-full bg-white"
                          style={{ height: '70vh', border: 0 }}
                        />
                      )
                    ) : isImg ? (
                      previewLoading || !previewBlobUrl ? (
                        <div className="bg-white p-8 flex items-center justify-center text-sm text-zinc-500" style={{ height: '40vh' }}>
                          <CircleNotch size={20} className="animate-spin mr-2" /> Afbeelding laden...
                        </div>
                      ) : (
                        <div className="bg-white p-3 flex justify-center max-h-[70vh] overflow-auto">
                          <img src={previewBlobUrl} alt={att.filename} className="max-w-full h-auto" />
                        </div>
                      )
                    ) : (
                      <div className="p-4 text-center text-sm text-zinc-500">Preview niet beschikbaar — gebruik de download-pijl rechts.</div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          {reply.has_attachments && (!Array.isArray(reply.incoming_attachments) || reply.incoming_attachments.length === 0) && reply.graph_message_id && (
            <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="attachments-hint">
              Deze email bevat bijlagen die nog niet zijn opgehaald. Klik op <strong>Volledig ophalen</strong> om ze zichtbaar te maken.
            </div>
          )}
        </div>

        {/* Sales Intent classification + Auto-draft */}
        {(intent.intent || cls.category) && (
          <div className="mb-4 p-3 rounded-md border border-violet-200 bg-violet-50/40" data-testid="intent-section">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700">🎯 Sales-Intent</span>
                {intent.intent ? (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${INTENT_PILL_STYLES[intent.intent] || 'bg-zinc-100'}`}>
                    {INTENT_LABELS[intent.intent] || intent.intent}
                    {intent.confidence != null && <span className="ml-1.5 opacity-70">{intent.confidence}%</span>}
                  </span>
                ) : (
                  <span className="text-[11px] italic text-zinc-500">nog niet geclassificeerd</span>
                )}
              </div>
              <button
                onClick={async () => {
                  try {
                    const r = await fetch(`${API}/email/inbox/${reply.reply_id}/classify-intent`, { method: 'POST', credentials: 'include' });
                    const d = await r.json();
                    if (d.ok) {
                      toast.success('Geclassificeerd');
                      onAction({ refresh: true });
                    } else toast.error(d.error || 'Mislukt');
                  } catch (e) { toast.error(e.message); }
                }}
                className="text-[10px] text-violet-600 hover:text-violet-800 flex items-center gap-1"
                data-testid="intent-classify-btn"
                title="Forceer (her)classificatie"
              >
                <ArrowsClockwise size={11} /> {intent.intent ? 'Herclassificeer' : 'Classificeer nu'}
              </button>
            </div>
            {intent.reasoning && (
              <p className="text-[11px] text-zinc-600 italic mb-1.5" data-testid="intent-reasoning">{intent.reasoning}</p>
            )}
            {intent.suggested_action && (
              <p className="text-[11px] text-violet-900 mb-2"><strong>Advies:</strong> {intent.suggested_action}</p>
            )}
            {intent.auto_draft_body && (
              <div className="mt-2 bg-white border border-violet-200 rounded p-2.5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">📝 Auto-draft (intent-based)</span>
                  <button
                    onClick={() => {
                      setEditing(true);
                      setEditSubject(intent.auto_draft_subject || '');
                      setEditBody(intent.auto_draft_body || '');
                      toast.success('Auto-draft geladen in editor');
                    }}
                    className="text-[10px] text-violet-600 hover:text-violet-800 font-medium"
                    data-testid="intent-load-draft-btn"
                  >
                    ↓ Laden in editor
                  </button>
                </div>
                {intent.auto_draft_subject && (
                  <div className="text-[11px] mb-1"><strong>Subject:</strong> {intent.auto_draft_subject}</div>
                )}
                <div className="text-[12px] text-zinc-700 whitespace-pre-wrap leading-relaxed" data-testid="intent-auto-draft-body">{intent.auto_draft_body}</div>
              </div>
            )}
          </div>
        )}

        {/* Draft response */}
        {draft && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold text-zinc-500 uppercase">AI Concept Antwoord</div>
              <div className="flex gap-2 items-center">
                <TranslateButton text={draft.body_text || draft.body_html} sourceLang={cls.detected_language}
                  onTranslated={setTranslatedDraft} />
                {!editing && reply.review_status !== 'verzonden' && (
                  <button
                    onClick={regenerateDraft}
                    disabled={regenerating}
                    data-testid="regenerate-draft-btn"
                    title="Herschrijf dit antwoord met de huidige AI-richtlijnen en recente admin-edits uit de leer-pool"
                    className="text-xs text-indigo-600 flex items-center gap-1 hover:text-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {regenerating
                      ? <CircleNotch size={12} className="animate-spin" />
                      : <ArrowsClockwise size={12} />}
                    {regenerating ? 'Herschrijven...' : 'Herschrijf met richtlijnen'}
                  </button>
                )}
                {!editing && reply.review_status === 'wacht_op_goedkeuring' && (
                  <button onClick={() => setEditing(true)} className="text-xs text-[#0055FF] flex items-center gap-1"><PencilSimple size={12} />Bewerken</button>
                )}
              </div>
            </div>
            {editing ? (
              <div className="border border-zinc-200 p-3 space-y-2">
                <input
                  value={editSubject}
                  onChange={e => setEditSubject(e.target.value)}
                  className="w-full border border-zinc-200 px-2 py-1 text-sm"
                />
                <AutoGrowTextarea
                  value={editBody}
                  onChange={e => setEditBody(e.target.value)}
                  className="w-full border border-zinc-200 px-2 py-1 text-sm"
                  data-testid="edit-body-textarea"
                />
              </div>
            ) : (
              <div
                data-testid="draft-body-preview"
                className="bg-blue-50/30 border border-blue-200 p-3 text-sm text-zinc-700 whitespace-pre-wrap max-h-[60vh] overflow-y-auto"
              >
                <div className="font-medium text-xs text-blue-800 mb-1">Onderwerp: {draft.subject}</div>
                {translatedDraft || draft.body_text || '-'}
              </div>
            )}
            <div className="text-[10px] text-zinc-400 mt-2 flex items-center gap-2 flex-wrap">
              <span>Gegenereerd door {draft.generated_by} op {draft.generated_at ? new Date(draft.generated_at).toLocaleString('nl-NL') : ''}</span>
              {draft.learned_from_count > 0 && (
                <span
                  data-testid="learned-from-badge"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200 font-bold"
                  title="Het AI-model heeft eerder verzonden (door admin bewerkte) antwoorden meegenomen als voorbeeld"
                >
                  ✓ Geleerd van {draft.learned_from_count} eerder{draft.learned_from_count === 1 ? '' : 'e'} verzonden antwoord{draft.learned_from_count === 1 ? '' : 'en'}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Attachment Manager */}
        {draft && (
          <div className="border border-indigo-200 bg-indigo-50/40 p-4 mb-4" data-testid="draft-attachment-manager">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold shrink-0">PDF</div>
                <div className="min-w-0 flex-1">
                  {attachment ? (
                    <>
                      <div className="text-sm font-medium text-zinc-800 truncate" data-testid="attachment-filename">{attachment.filename}</div>
                      <div className="text-xs text-zinc-500">
                        {attachment.target_lang?.toUpperCase()} | {attachment.translation_source === 'cache' ? 'Gecachede vertaling' : attachment.translation_source === 'original' ? 'Origineel (master)' : attachment.translation_source === 'fallback_master' ? 'Fallback (master)' : 'Nieuw vertaald'}{attachment.file_size ? ` | ${Math.round(attachment.file_size / 1024)} KB` : ''}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-zinc-500 italic">Geen bijlage geselecteerd</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {attachment && (
                  <button onClick={handleTogglePreview} data-testid="preview-attachment-btn"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors">
                    <Eye size={12} /> {previewUrl ? 'Sluit preview' : 'Preview'}
                  </button>
                )}
                <button onClick={() => setShowPicker(true)} data-testid="replace-attachment-btn"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[#0055FF] border border-blue-200 hover:bg-blue-50 transition-colors">
                  <BookOpen size={12} /> {attachment ? 'Vervangen' : 'Kies document'}
                </button>
                {attachment && (
                  <button onClick={handleRemoveAttachment} disabled={removingAttachment} data-testid="remove-attachment-btn"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors">
                    {removingAttachment ? <CircleNotch size={12} className="animate-spin" /> : <Trash size={12} />} Verwijderen
                  </button>
                )}
              </div>
            </div>

            {previewUrl && (
              <div className="mt-3 border border-indigo-200 bg-white rounded-sm overflow-hidden" data-testid="attachment-preview-frame">
                <iframe title="PDF preview" src={previewUrl} className="w-full h-[480px] border-0" />
                <div className="px-3 py-1.5 text-[10px] text-zinc-500 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between">
                  <span>Inline preview — wordt zo verzonden naar de klant.</span>
                  <a href={previewUrl} target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline">Open in nieuw tabblad</a>
                </div>
              </div>
            )}
            <p className="text-[10px] text-zinc-400 mt-2">Document wordt automatisch vertaald naar de doeltaal van de klant en als PDF bijgevoegd.</p>
          </div>
        )}

        {/* Actions */}
        {reply.review_status === 'wacht_op_goedkeuring' && draft && (
          <div className="flex gap-2 mb-4">
            <button data-testid="approve-btn"
              onClick={() => doAction('approve', editing ? { edited_draft: { subject: editSubject, body_text: editBody, body_html: `<p>${editBody.replace(/\n/g, '</p><p>')}</p>` } } : {})}
              disabled={actionLoading === 'approve'}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {actionLoading === 'approve' ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={16} />}
              {editing ? 'Opslaan & Verzenden' : 'Goedkeuren & Verzenden'}
            </button>
            <button onClick={() => setShowReject(!showReject)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 border border-red-200 hover:bg-red-50 transition-colors">
              <XCircle size={16} /> Afwijzen
            </button>
          </div>
        )}

        {showReject && (
          <div className="mb-4 space-y-2">
            <textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} placeholder="Reden voor afwijzing..." rows={2} className="w-full border border-zinc-200 px-2 py-1 text-sm" />
            <button onClick={() => doAction('reject', { notes: rejectNotes })} disabled={actionLoading === 'reject'}
              className="px-3 py-1.5 text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Bevestig Afwijzing</button>
          </div>
        )}

        {reply.review_status === 'goedgekeurd' && (
          <button onClick={() => doAction('send')} disabled={actionLoading === 'send'}
            className="flex items-center gap-2 px-4 py-2 bg-[#0055FF] text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors mb-4">
            {actionLoading === 'send' ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneRight size={16} />}
            Verzenden
          </button>
        )}

        {(reply.review_status === 'nieuw' || reply.review_status === 'afgewezen') && (
          <button onClick={() => doAction('reclassify')} disabled={actionLoading === 'reclassify'}
            className="flex items-center gap-2 px-3 py-1.5 text-xs border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50 transition-colors">
            {actionLoading === 'reclassify' ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
            (Her)classificeren met AI
          </button>
        )}
      </div>
      {showPicker && (
        <AttachmentPickerModal
          replyId={reply.reply_id}
          currentDocId={attachment?.doc_id}
          onClose={() => setShowPicker(false)}
          onPicked={handlePickerPicked}
        />
      )}
    </div>
  );
}

// Textarea that auto-resizes with its content (no inner scrollbar tot 70vh)
function AutoGrowTextarea({ value, onChange, className = '', minRows = 4, ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, Math.round(window.innerHeight * 0.7))}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={minRows}
      className={`${className} resize-none overflow-y-auto`}
      style={{ minHeight: `${minRows * 1.5}rem` }}
      {...rest}
    />
  );
}

function LiveSyncBadge({ autoSync }) {
  // Re-render every 30s so "Xm geleden" stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  if (!autoSync) return null;

  const enabled = autoSync.enabled && autoSync.graph_configured && autoSync.running;
  const lastRun = autoSync.last_run ? new Date(autoSync.last_run) : null;
  const ageSec = lastRun ? Math.floor((Date.now() - lastRun.getTime()) / 1000) : null;
  const ageLabel = (() => {
    if (ageSec === null) return 'nog geen run';
    if (ageSec < 60) return `${ageSec}s geleden`;
    const m = Math.floor(ageSec / 60);
    if (m < 60) return `${m}m geleden`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}u geleden`;
    return `${Math.floor(h / 24)}d geleden`;
  })();

  // Stale = older than 2x interval (gap longer than expected)
  const intervalSec = (autoSync.interval_minutes || 2) * 60;
  const stale = ageSec !== null && ageSec > intervalSec * 2;

  if (autoSync.last_error) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 border border-red-200 bg-red-50 rounded-sm" data-testid="live-sync-badge" title={autoSync.last_error}>
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        <span className="text-[11px] font-semibold text-red-700 uppercase tracking-wider">Sync fout</span>
        <span className="text-[10px] text-red-500">| {ageLabel}</span>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 border border-zinc-200 bg-zinc-50 rounded-sm" data-testid="live-sync-badge">
        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
        <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Auto-sync uit</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-sm ${stale ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}
      data-testid="live-sync-badge"
      title={`Auto-sync elke ${autoSync.interval_minutes} min via MS Graph`}
    >
      <span className={`relative flex w-2 h-2`}>
        {!stale && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${stale ? 'bg-amber-500' : 'bg-emerald-500'}`} />
      </span>
      <span className={`text-[11px] font-semibold uppercase tracking-wider ${stale ? 'text-amber-800' : 'text-emerald-700'}`}>Live</span>
      <span className={`text-[10px] ${stale ? 'text-amber-600' : 'text-emerald-600'}`}>| Laatste sync: {ageLabel}</span>
    </div>
  );
}

export default function InboxPage() {
  const [inboxItems, setInboxItems] = useState([]);
  const [inboxTotal, setInboxTotal] = useState(0);
  const [inboxStats, setInboxStats] = useState(null);
  const [inboxFilter, setInboxFilter] = useState({ status: '', category: '', mailbox: '' });
  const [selectedReply, setSelectedReply] = useState(null);
  const [replyDetail, setReplyDetail] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [classifyingIntents, setClassifyingIntents] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [bulkBackfilling, setBulkBackfilling] = useState(false);
  const [bulkBackfillStatus, setBulkBackfillStatus] = useState(null); // { processed, missing, failed, remaining }
  const [graphStatus, setGraphStatus] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [autoSync, setAutoSync] = useState(null);

  const fetchAutoSync = useCallback(() => {
    fetch(`${API}/email/inbox/auto-sync/settings`, { credentials: 'include' })
      .then(r => r.json()).then(setAutoSync).catch(() => {});
  }, []);

  const [inboxByMailbox, setInboxByMailbox] = useState([]);

  const fetchInbox = useCallback(() => {
    const params = new URLSearchParams();
    if (inboxFilter.status) params.set('status', inboxFilter.status);
    if (inboxFilter.category) params.set('category', inboxFilter.category);
    if (inboxFilter.mailbox) params.set('mailbox', inboxFilter.mailbox);
    fetch(`${API}/email/inbox?${params}`, { credentials: 'include' })
      .then(r => r.json()).then(d => {
        setInboxItems(d.replies || []);
        setInboxTotal(d.total || 0);
        setInboxByMailbox(d.by_mailbox || []);
      }).catch(() => {});
  }, [inboxFilter]);

  const fetchInboxStats = useCallback(() => {
    fetch(`${API}/email/inbox/stats`, { credentials: 'include' })
      .then(r => r.json()).then(setInboxStats).catch(() => {});
  }, []);

  const fetchReplyDetail = (replyId) => {
    fetch(`${API}/email/inbox/${replyId}`, { credentials: 'include' })
      .then(r => r.json()).then(setReplyDetail).catch(() => {});
  };

  useEffect(() => {
    fetchInbox(); fetchInboxStats();
    setSelectedIds(new Set());
    fetch(`${API}/graph/status`, { credentials: 'include' }).then(r => r.json()).then(setGraphStatus).catch(() => {});
  }, [fetchInbox, fetchInboxStats]);

  // Live polling: poll auto-sync status every 30s; when last_run changes, refresh inbox so new emails appear automatically
  useEffect(() => {
    fetchAutoSync();
    const t = setInterval(fetchAutoSync, 30000);
    return () => clearInterval(t);
  }, [fetchAutoSync]);

  useEffect(() => {
    if (autoSync?.last_run) {
      fetchInbox();
      fetchInboxStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSync?.last_run]);

  const syncInbox = async () => {
    setSyncing(true);
    try {
      await fetch(`${API}/graph/inbox/sync`, { method: 'POST', credentials: 'include' });
      fetchInbox(); fetchInboxStats();
    } catch { /* ignore */ }
    setSyncing(false);
  };

  const [generatingMissing, setGeneratingMissing] = useState(false);
  const [missingProgress, setMissingProgress] = useState(null);
  // Genereer concepten voor alle mails zonder draft binnen het ACTIEVE profiel.
  // Loopt in batches van 3 (sync LLM calls van ~6-10s) tot remaining=0.
  const generateMissingDrafts = async () => {
    if (generatingMissing) return;
    setGeneratingMissing(true);
    let total = 0;
    try {
      let safety = 50;
      while (safety-- > 0) {
        const res = await fetch(`${API}/email/inbox/bulk-regenerate`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'missing', limit: 3 }),
        });
        if (!res.ok) { toast.error('Generatie mislukt'); break; }
        const d = await res.json();
        total += d.processed || 0;
        setMissingProgress({ done: total, remaining: d.remaining ?? 0 });
        if ((d.total_considered || 0) === 0 || (d.remaining ?? 0) === 0) break;
      }
      toast.success(`${total} concept(en) klaar voor review`);
      fetchInbox(); fetchInboxStats();
    } catch (e) {
      toast.error(`Netwerkfout: ${e.message || 'onbekend'}`);
    }
    setGeneratingMissing(false);
    setTimeout(() => setMissingProgress(null), 4000);
  };

  const runIntentClassifier = async () => {
    setClassifyingIntents(true);
    try {
      const r = await fetch(`${API}/email/inbox/classify-unclassified`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`${d.classified || 0} replies geclassificeerd${d.errors?.length ? ` (${d.errors.length} fouten)` : ''}`);
        fetchInbox();
      } else {
        toast.error(d.detail || 'Classificatie mislukt');
      }
    } catch (e) {
      toast.error(e.message || 'Classificatie mislukt');
    }
    setClassifyingIntents(false);
  };

  const simulateBatch = async () => {
    setSimulating(true);
    try {
      await fetch(`${API}/email/inbox/simulate-batch`, { method: 'POST', credentials: 'include' });
      fetchInbox(); fetchInboxStats();
    } catch { /* ignore */ }
    setSimulating(false);
  };

  const runBulkBackfill = async () => {
    if (!window.confirm('Haal volledige body + bijlagen op voor ALLE oude emails. Dit kan enkele minuten duren afhankelijk van het aantal. Doorgaan?')) return;
    setBulkBackfilling(true);
    setBulkBackfillStatus({ processed: 0, missing: 0, failed: 0, remaining: '?' });
    const CHUNK = 5;
    let totProcessed = 0, totMissing = 0, totFailed = 0;
    let safety = 200; // hard cap (totaal max 1000 items)
    try {
      while (safety-- > 0) {
        const r = await fetch(`${API}/email/inbox/bulk-backfill`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: CHUNK }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          window.alert(`Bulk-backfill stopte: ${err.detail || r.statusText}`);
          break;
        }
        const d = await r.json();
        totProcessed += d.processed;
        totMissing += d.missing;
        totFailed += d.failed;
        setBulkBackfillStatus({
          processed: totProcessed, missing: totMissing, failed: totFailed, remaining: d.remaining,
        });
        if (d.total_considered === 0) break;
      }
      window.alert(`Klaar: ${totProcessed} bijgewerkt · ${totMissing} verlopen · ${totFailed} mislukt`);
      fetchInbox(); fetchInboxStats();
    } catch (e) {
      window.alert(`Verbindingsfout: ${e.message || 'onbekend'}`);
    }
    setBulkBackfilling(false);
  };

  const openDetail = (reply) => {
    setSelectedReply(reply.reply_id);
    fetchReplyDetail(reply.reply_id);
  };

  const deleteInboxItem = async (replyId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Weet je zeker dat je deze email permanent wilt verwijderen?')) return;
    try {
      const res = await fetch(`${API}/email/inbox/${replyId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      if (selectedReply === replyId) { setReplyDetail(null); setSelectedReply(null); }
      setSelectedIds(prev => { const n = new Set(prev); n.delete(replyId); return n; });
      fetchInbox(); fetchInboxStats();
    } catch { /* ignore */ }
  };

  const toggleSelect = (replyId, e) => {
    if (e) e.stopPropagation();
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(replyId)) n.delete(replyId); else n.add(replyId);
      return n;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === inboxItems.length && inboxItems.length > 0) return new Set();
      return new Set(inboxItems.map(r => r.reply_id));
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Weet je zeker dat je ${ids.length} email(s) permanent wilt verwijderen?`)) return;
    setBulkDeleting(true);
    try {
      await fetch(`${API}/email/inbox/bulk-delete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply_ids: ids }),
      });
      setSelectedIds(new Set());
      if (selectedReply && ids.includes(selectedReply)) { setReplyDetail(null); setSelectedReply(null); }
      fetchInbox(); fetchInboxStats();
    } catch { /* ignore */ }
    setBulkDeleting(false);
  };

  const bulkDeleteCategory = async (category, label) => {
    if (!window.confirm(`Alle emails in categorie "${label}" permanent verwijderen?`)) return;
    setBulkDeleting(true);
    try {
      await fetch(`${API}/email/inbox/bulk-delete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      });
      setSelectedIds(new Set());
      fetchInbox(); fetchInboxStats();
    } catch { /* ignore */ }
    setBulkDeleting(false);
  };

  return (
    <div className="animate-fade-in" data-testid="inbox-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl font-black tracking-tight">E-mail</h1>
            <LiveSyncBadge autoSync={autoSync} />
          </div>
          <p className="text-sm text-zinc-500 mt-1">Inkomende e-mails</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={syncInbox} disabled={syncing} data-testid="sync-inbox-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors">
            {syncing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
            {syncing ? 'Syncing...' : 'Sync Inbox'}
          </button>
          <button onClick={runIntentClassifier} disabled={classifyingIntents} data-testid="classify-intents-btn"
            title="Bepaal voor alle ongeklassificeerde replies de sales-intent + genereer auto-drafts"
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 transition-colors">
            {classifyingIntents ? <CircleNotch size={12} className="animate-spin" /> : <Question size={12} />}
            {classifyingIntents ? 'Classificeren...' : 'AI Intent-scan'}
          </button>
          <button onClick={generateMissingDrafts} disabled={generatingMissing} data-testid="generate-missing-drafts-btn"
            title="Genereer een AI-concept voor elke mail die nog geen concept heeft"
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-60 transition-colors">
            {generatingMissing ? <CircleNotch size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
            {generatingMissing
              ? (missingProgress ? `Concepten... ${missingProgress.done}/${missingProgress.done + missingProgress.remaining}` : 'Concepten genereren...')
              : 'Genereer ontbrekende concepten'}
          </button>
          <button onClick={runBulkBackfill} disabled={bulkBackfilling} data-testid="bulk-backfill-btn"
            title="Haal volledige body + bijlagen op voor alle oude emails die nog ingekort zijn"
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors">
            {bulkBackfilling ? <CircleNotch size={12} className="animate-spin" /> : <BookOpen size={12} />}
            {bulkBackfilling
              ? (bulkBackfillStatus
                  ? `Bezig... ${bulkBackfillStatus.processed} klaar${typeof bulkBackfillStatus.remaining === 'number' ? `, nog ${bulkBackfillStatus.remaining}` : ''}`
                  : 'Ophalen...')
              : 'Volledige body ophalen'}
          </button>
          <button onClick={simulateBatch} disabled={simulating} data-testid="simulate-batch-btn"
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-zinc-800 text-white hover:bg-zinc-900 disabled:opacity-50 transition-colors">
            {simulating ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} />}
            {simulating ? 'Simuleren...' : 'Simuleer'}
          </button>
        </div>
      </div>

      {/* Graph Status */}
      {graphStatus?.connected && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 flex items-center gap-3 text-xs text-emerald-800" data-testid="graph-inbox-status">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <strong>Microsoft Graph — Live</strong>
          {graphStatus.mailboxes?.map((m, i) => (
            <span key={i} className="px-2 py-0.5 bg-emerald-100 border border-emerald-200">{m.email} ({m.inbox_unread} ongelezen)</span>
          ))}
        </div>
      )}

      {/* Stats */}
      {inboxStats && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="inbox-stats">
          {[
            { l: 'Totaal', v: inboxStats.total, c: 'text-zinc-700' },
            { l: 'Nieuw', v: inboxStats.nieuw, c: 'text-zinc-600' },
            { l: 'Wacht op review', v: inboxStats.wacht_op_goedkeuring, c: inboxStats.wacht_op_goedkeuring > 0 ? 'text-amber-600' : 'text-zinc-400' },
            { l: 'Goedgekeurd', v: inboxStats.goedgekeurd, c: 'text-emerald-600' },
            { l: 'Afgewezen', v: inboxStats.afgewezen, c: 'text-red-500' },
            { l: 'Verzonden', v: inboxStats.verzonden, c: 'text-blue-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white p-3 text-center">
              <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-mailbox chip-filter — shows all mailboxes that have received mails for this profile.
          Clicking a chip filters the inbox to that mailbox; clicking "Alle" resets. */}
      {inboxByMailbox && inboxByMailbox.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="inbox-mailbox-chips">
          <span className="text-[11px] text-zinc-500 mr-1">Postbussen:</span>
          <button
            onClick={() => setInboxFilter(f => ({ ...f, mailbox: '' }))}
            data-testid="inbox-mailbox-chip-all"
            className={`text-[11px] px-2 py-0.5 rounded-sm transition-colors ${
              !inboxFilter.mailbox
                ? 'bg-zinc-800 text-white'
                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
            }`}
          >
            Alle ({inboxByMailbox.reduce((s, m) => s + m.count, 0)})
          </button>
          {inboxByMailbox.map(m => (
            <button
              key={m.mailbox}
              onClick={() => setInboxFilter(f => ({ ...f, mailbox: m.mailbox }))}
              data-testid={`inbox-mailbox-chip-${m.mailbox}`}
              className={`text-[11px] px-2 py-0.5 rounded-sm transition-colors ${
                inboxFilter.mailbox === m.mailbox
                  ? 'bg-[#0055FF] text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              title={m.mailbox}
            >
              {m.mailbox} ({m.count})
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <CollapsibleSection
        id="inbox-filters"
        title={`Filters${(inboxFilter.status || inboxFilter.category) ? ' (actief)' : ''}`}
        icon={Funnel}
        accent={(inboxFilter.status || inboxFilter.category) ? 'text-[#0055FF]' : 'text-zinc-500'}
        storageKey="inbox:collapsedSections"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Select value={inboxFilter.status || 'all'} onValueChange={v => setInboxFilter(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="rounded-sm text-sm w-48" data-testid="inbox-filter-status"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statussen</SelectItem>
              <SelectItem value="wacht_op_goedkeuring">Wacht op goedkeuring</SelectItem>
              <SelectItem value="nieuw">Nieuw</SelectItem>
              <SelectItem value="goedgekeurd">Goedgekeurd</SelectItem>
              <SelectItem value="afgewezen">Afgewezen</SelectItem>
              <SelectItem value="verzonden">Verzonden</SelectItem>
            </SelectContent>
          </Select>
          <Select value={inboxFilter.category || 'all'} onValueChange={v => setInboxFilter(f => ({ ...f, category: v === 'all' ? '' : v }))}>
            <SelectTrigger className="rounded-sm text-sm w-44" data-testid="inbox-filter-category"><SelectValue placeholder="Alle categorien" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle categorien</SelectItem>
              {Object.entries(CATEGORY_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => { fetchInbox(); fetchInboxStats(); }} className="p-2 hover:bg-zinc-100 rounded-sm ml-auto" title="Vernieuwen">
            <ArrowsClockwise size={14} />
          </button>
        </div>
      </CollapsibleSection>

      {/* Bulk Action Bar — inline when there's a selection; collapsible cleanup otherwise */}
      {selectedIds.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="bulk-action-bar">
          <span className="text-xs text-zinc-600 mr-1">{selectedIds.size} geselecteerd</span>
          <button
            onClick={bulkDelete}
            disabled={bulkDeleting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            data-testid="bulk-delete-btn"
          >
            {bulkDeleting ? <CircleNotch size={12} className="animate-spin" /> : <Trash size={12} />}
            Verwijder selectie
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 transition-colors"
          >
            Selectie wissen
          </button>
        </div>
      ) : (
        <CollapsibleSection
          id="inbox-bulk-cleanup"
          title="Snel opruimen"
          icon={ListChecks}
          accent="text-zinc-500"
          storageKey="inbox:collapsedSections"
        >
          <div className="flex flex-wrap items-center gap-2" data-testid="bulk-action-bar">
            <button
              onClick={() => bulkDeleteCategory('delivery_failure', 'Bounces')}
              disabled={bulkDeleting}
              className="px-2.5 py-1 text-xs text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors"
              data-testid="bulk-delete-bounces-btn"
            >
              <Trash size={11} className="inline mr-1" />Alle bounces
            </button>
            <button
              onClick={() => bulkDeleteCategory('spam', 'Spam')}
              disabled={bulkDeleting}
              className="px-2.5 py-1 text-xs text-zinc-700 border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
              data-testid="bulk-delete-spam-btn"
            >
              <Trash size={11} className="inline mr-1" />Alle spam
            </button>
            <button
              onClick={() => bulkDeleteCategory('out_of_office', 'Afwezig')}
              disabled={bulkDeleting}
              className="px-2.5 py-1 text-xs text-zinc-700 border border-zinc-200 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
              data-testid="bulk-delete-ooo-btn"
            >
              <Trash size={11} className="inline mr-1" />Alle afwezig
            </button>
            <button
              onClick={() => bulkDeleteCategory('unsubscribe', 'Afmeldingen')}
              disabled={bulkDeleting}
              className="px-2.5 py-1 text-xs text-orange-700 border border-orange-200 hover:bg-orange-50 disabled:opacity-50 transition-colors"
              data-testid="bulk-delete-unsub-btn"
            >
              <Trash size={11} className="inline mr-1" />Alle afmeldingen
            </button>
          </div>
        </CollapsibleSection>
      )}

      {/* Table */}
      <div className="border border-zinc-200 overflow-hidden" data-testid="inbox-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={inboxItems.length > 0 && selectedIds.size === inboxItems.length}
                  onChange={toggleSelectAll}
                  className="cursor-pointer accent-[#0055FF]"
                  data-testid="select-all-inbox"
                  title="Selecteer alle"
                />
              </th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 w-8"></th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Categorie</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Van</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Onderwerp</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Mailbox</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Urgentie</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Status</th>
              <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Ontvangen</th>
              <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {inboxItems.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-zinc-400">
                <Envelope size={32} className="mx-auto mb-2 text-zinc-300" />
                <div className="text-sm">Geen inkomende emails{inboxFilter.status || inboxFilter.category ? ' met deze filters' : ''}.</div>
                <div className="text-xs mt-1">Klik op "Sync Inbox" om emails op te halen.</div>
              </td></tr>
            ) : inboxItems.map(r => {
              const cat = CATEGORY_STYLES[r.ai_classification?.category] || CATEGORY_STYLES.other;
              const rev = REVIEW_STATUS_STYLES[r.review_status] || REVIEW_STATUS_STYLES.nieuw;
              const isSelected = selectedReply === r.reply_id;
              return (
                <tr key={r.reply_id} onClick={() => openDetail(r)}
                  className={`border-b border-zinc-100 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : selectedIds.has(r.reply_id) ? 'bg-amber-50/40' : 'hover:bg-zinc-50'} ${r.review_status === 'wacht_op_goedkeuring' ? 'font-medium' : ''}`}
                  data-testid={`inbox-row-${r.reply_id}`}>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.reply_id)}
                        onChange={(e) => toggleSelect(r.reply_id, e)}
                        className="cursor-pointer accent-[#0055FF]"
                        data-testid={`select-inbox-${r.reply_id}`}
                      />
                      <button
                        onClick={(e) => deleteInboxItem(r.reply_id, e)}
                        className="md:hidden p-1.5 -mr-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors rounded-sm"
                        title="Verwijderen"
                        aria-label="Verwijderen"
                        data-testid={`mobile-delete-inbox-${r.reply_id}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.ai_classification?.sentiment === 'positive' && <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
                    {r.ai_classification?.sentiment === 'negative' && <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />}
                    {r.ai_classification?.sentiment === 'neutral' && <span className="w-2 h-2 rounded-full bg-zinc-300 inline-block" />}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={`rounded-sm px-2 py-0.5 text-[10px] font-medium border ${cat.bg}`}>{cat.label}</Badge>
                      {(r.ai_classification?.secondary_intents || []).map(si => (
                        <IntentIconBadge key={si} intent={si} />
                      ))}
                      {r.intent_classification?.intent && (
                        <Badge className={`rounded-sm px-1.5 py-0.5 text-[9px] font-bold border ${INTENT_PILL_STYLES[r.intent_classification.intent] || 'bg-zinc-100 text-zinc-700 border-zinc-300'}`}
                               title={`${r.intent_classification.confidence ?? '?'}% — ${r.intent_classification.reasoning || ''}`}
                               data-testid={`intent-pill-${r.reply_id}`}>
                          {INTENT_LABELS[r.intent_classification.intent] || r.intent_classification.intent}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-sm">{r.from_name || r.from_email?.split('@')[0]}</div>
                    <div className="text-[11px] text-zinc-400">{r.from_email}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm max-w-[200px] truncate">{r.subject}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className="text-[10px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5"
                      title={r.received_mailbox || ''}
                      data-testid={`inbox-row-mailbox-${r.reply_id}`}
                    >
                      {r.received_mailbox ? r.received_mailbox.split('@')[0] : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs ${URGENCY_COLORS[r.ai_classification?.urgency] || 'text-zinc-400'}`}>
                      {r.ai_classification?.urgency || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${rev.bg}`}>{rev.label}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500">
                    {r.received_at ? new Date(r.received_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={(e) => deleteInboxItem(r.reply_id, e)}
                      className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors rounded-sm"
                      title="Verwijderen"
                      data-testid={`delete-inbox-${r.reply_id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {inboxTotal > 0 && <div className="text-xs text-zinc-400 mt-2 px-1">{inboxTotal} totaal</div>}

      {/* Detail Panel */}
      {replyDetail && (
        <InboxDetailPanel reply={replyDetail} onClose={() => { setReplyDetail(null); setSelectedReply(null); }}
          onAction={(kind) => {
            if (kind === 'regenerated') {
              // Panel open houden maar data verversen zodat de nieuwe draft zichtbaar wordt
              fetchInbox(); fetchInboxStats();
              // Herlaad het specifieke reply detail
              fetch(`${API}/email/inbox/${replyDetail.reply_id}`, { credentials: 'include' })
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data) setReplyDetail(data); })
                .catch(() => {});
              return;
            }
            setReplyDetail(null); setSelectedReply(null); fetchInbox(); fetchInboxStats();
          }} />
      )}
    </div>
  );
}
